"use client"
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { getCustomers, getUsers, createTenantUser, createTenantAdmin, updateTenant, getUserDetail, updateUser, deleteUser, getTaxesAndFees, getTaxeAndFee, createTaxAndFee, updateTaxAndFee, deleteTaxAndFee, getTenants, getTenantRoles, getTenant, getTenantUserDetail } from "../api";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../../../contexts/NotificationContext";

export const useGetCustomers = (page = 1, pageSize = 10) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["companies", page, pageSize],
    queryFn: () => getCustomers(page, pageSize),
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useGetUsers = (page = 1, pageSize = 10) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["users", page, pageSize],
    queryFn: () => getUsers(page, pageSize),
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useGetTenantRoles = (tenantId) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["tenant-roles", tenantId],
    queryFn: () => getTenantRoles(tenantId),
    enabled: !!tenantId,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useGetTenants = (page = 1, pageSize = 10, search = '', status = 'all') => {
  const { data, isLoading, status: queryStatus, isError, error, isSuccess } = useQuery({
    queryKey: ["tenants", page, pageSize, search, status],
    queryFn: () => getTenants(page, pageSize, search, status),
  });

  return {
    data,
    status: queryStatus,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};


export const useGetTenant = (id, type = null, page = 1, pageSize = 10, additionalParams = {}) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["tenant", id, type, page, pageSize, additionalParams],
    queryFn: () => getTenant(id, type, page, pageSize, additionalParams),
    enabled: !!id,
    placeholderData: keepPreviousData,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};


export const useCreateTelantAdmin = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (formData) => createTenantAdmin(formData),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });

      console.log("data123123", data)
      // Show success notification
      showNotification('success', 'Customer created successfully!');

      // Navigate immediately without setTimeout
      navigate(`/tenant/add/${data?.code}`);
    },
    onError: (error) => {
      console.error("Error creating tenant admin:", error);

      // Show error notification
      let errorMessage = 'Error creating customer. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};

export const useGetUserDetail = (id) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["user-detail", id],
    queryFn: () => getUserDetail(id),
    enabled: !!id,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useCreateTenantUser = (tenantId, onSuccessCallback) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (data) => createTenantUser(data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["tenant", tenantId] });
      showNotification('success', 'User created successfully!');
      if (onSuccessCallback) {
        onSuccessCallback(data);
      } else {
        navigate(`/tenant`);
      }
    },
    onError: (error) => {
      console.error("Error creating tenant user:", error);

      let errorMessage = 'Error creating user. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useUpdateTenant = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => updateTenant(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tenants"] });
      showNotification('success', 'Tenant updated successfully!');
    },
    onError: (error) => {
      console.error("Error updating tenant:", error);

      let errorMessage = 'Error updating tenant. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useGetTenantUserDetail = (id, tenantId) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["tenant-user-detail", id, tenantId],
    queryFn: () => getTenantUserDetail(id, tenantId),
    enabled: !!id && !!tenantId,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};

export const useUpdateUser = (tenantId) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => updateUser(tenantId, id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      await queryClient.invalidateQueries({ queryKey: ["user-detail"] });

      // Show success notification
      showNotification('success', 'Customer updated successfully!');

    },
    onError: (error) => {
      console.error("Error updating user:", error);

      // Show error notification
      let errorMessage = 'Error updating customer. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (id) => deleteUser(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });

      // Show success notification
      showNotification('success', 'Customer deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting user:", error);

      // Show error notification
      let errorMessage = 'Error deleting customer. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useGetTaxesAndFees = () => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["taxes-and-fees"],
    queryFn: () => getTaxesAndFees(),
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};



export const useGetTaxeAndFee = (id) => {
  const { data, isLoading, status, isError, error, isSuccess } = useQuery({
    queryKey: ["taxe-and-fee", id],
    queryFn: () => getTaxeAndFee(id),
    enabled: !!id,
  });

  return {
    data,
    status,
    isLoading,
    isError,
    error,
    isSuccess,
  };
};


export const useCreateTaxAndFee = ({ onClose, type }) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  const mutation = useMutation({
    mutationFn: (formData) => createTaxAndFee(formData),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["taxes-and-fees"] });
      await queryClient.invalidateQueries({ queryKey: ["sales-locations"] });
      await queryClient.invalidateQueries({ queryKey: ["channel"] });
      showNotification('success', `${type} created successfully!`);
      if (onClose) onClose();
    },
    onError: (error) => {
      console.error("Error creating taxe and fee:", error);
      let errorMessage = `Error creating ${type}. Please try again.`;
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });

  return mutation;
};

export const useUpdateTaxAndFee = ({ onClose, type }) => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: ({ id, data }) => updateTaxAndFee(id, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["taxes-and-fees"] });
      await queryClient.invalidateQueries({ queryKey: ["sales-locations"] });
      await queryClient.invalidateQueries({ queryKey: ["channel"] });

      // Show success notification
      showNotification('success', `${type} updated successfully!`);

      // Navigate immediately without setTimeout
      if (onClose) onClose();
    },
    onError: (error) => {
      console.error("Error updating taxe and fee:", error);

      // Show error notification
      let errorMessage = `Error updating ${type}. Please try again.`;
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};

export const useDeleteTaxAndFee = () => {
  const queryClient = useQueryClient();
  const { showNotification } = useNotification();

  return useMutation({
    mutationFn: (id) => deleteTaxAndFee(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["taxes-and-fees"] });
      await queryClient.invalidateQueries({ queryKey: ["sales-locations"] });
      await queryClient.invalidateQueries({ queryKey: ["channel"] });

      // Show success notification
      showNotification('success', 'Tax/Fee deleted successfully!');
    },
    onError: (error) => {
      console.error("Error deleting tax/fee:", error);

      // Show error notification
      let errorMessage = 'Error deleting tax/fee. Please try again.';
      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      showNotification('danger', errorMessage);
    },
  });
};